/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */


import {Injector, NgModuleRef} from '@angular/core';
import {Observable} from 'rxjs/Observable';
import {Observer} from 'rxjs/Observer';
import {from} from 'rxjs/observable/from';
import {of } from 'rxjs/observable/of';
import {_catch} from 'rxjs/operator/catch';
import {concatAll} from 'rxjs/operator/concatAll';
import {first} from 'rxjs/operator/first';
import {map} from 'rxjs/operator/map';
import {mergeMap} from 'rxjs/operator/mergeMap';
import {EmptyError} from 'rxjs/util/EmptyError';

import {LoadedRouterConfig, Route, Routes} from './config';
import {RouterConfigLoader} from './router_config_loader';
import {PRIMARY_OUTLET, Params, defaultUrlMatcher, navigationCancelingError} from './shared';
import {UrlSegment, UrlSegmentGroup, UrlSerializer, UrlTree} from './url_tree';
import {andObservables, forEach, waitForMap, wrapIntoObservable} from './utils/collection';
class NoMatch {
public segmentGroup: UrlSegmentGroup|null;
/**
 * @param {?=} segmentGroup
 */
constructor(segmentGroup?: UrlSegmentGroup) { this.segmentGroup = segmentGroup || null; }
}

function NoMatch_tsickle_Closure_declarations() {
/** @type {?} */
NoMatch.prototype.segmentGroup;
}

class AbsoluteRedirect {
/**
 * @param {?} urlTree
 */
constructor(public urlTree: UrlTree) {}
}

function AbsoluteRedirect_tsickle_Closure_declarations() {
/** @type {?} */
AbsoluteRedirect.prototype.urlTree;
}

/**
 * @param {?} segmentGroup
 * @return {?}
 */
function noMatch(segmentGroup: UrlSegmentGroup): Observable<UrlSegmentGroup> {
  return new Observable<UrlSegmentGroup>(
      (obs: Observer<UrlSegmentGroup>) => obs.error(new NoMatch(segmentGroup)));
}
/**
 * @param {?} newTree
 * @return {?}
 */
function absoluteRedirect(newTree: UrlTree): Observable<any> {
  return new Observable<UrlSegmentGroup>(
      (obs: Observer<UrlSegmentGroup>) => obs.error(new AbsoluteRedirect(newTree)));
}
/**
 * @param {?} redirectTo
 * @return {?}
 */
function namedOutletsRedirect(redirectTo: string): Observable<any> {
  return new Observable<UrlSegmentGroup>(
      (obs: Observer<UrlSegmentGroup>) => obs.error(new Error(
          `Only absolute redirects can have named outlets. redirectTo: '${redirectTo}'`)));
}
/**
 * @param {?} route
 * @return {?}
 */
function canLoadFails(route: Route): Observable<LoadedRouterConfig> {
  return new Observable<LoadedRouterConfig>(
      (obs: Observer<LoadedRouterConfig>) => obs.error(navigationCancelingError(
          `Cannot load children because the guard of the route "path: '${route.path}'" returned false`)));
}
/**
 * Returns the `UrlTree` with the redirection applied.
 * 
 * Lazy modules are loaded along the way.
 * @param {?} moduleInjector
 * @param {?} configLoader
 * @param {?} urlSerializer
 * @param {?} urlTree
 * @param {?} config
 * @return {?}
 */
export function applyRedirects(
    moduleInjector: Injector, configLoader: RouterConfigLoader, urlSerializer: UrlSerializer,
    urlTree: UrlTree, config: Routes): Observable<UrlTree> {
  return new ApplyRedirects(moduleInjector, configLoader, urlSerializer, urlTree, config).apply();
}
class ApplyRedirects {
private allowRedirects: boolean = true;
private ngModule: NgModuleRef<any>;
/**
 * @param {?} moduleInjector
 * @param {?} configLoader
 * @param {?} urlSerializer
 * @param {?} urlTree
 * @param {?} config
 */
constructor(
      moduleInjector: Injector,
private configLoader: RouterConfigLoader,
private urlSerializer: UrlSerializer,
private urlTree: UrlTree,
private config: Routes) {
    this.ngModule = moduleInjector.get(NgModuleRef);
  }
/**
 * @return {?}
 */
apply(): Observable<UrlTree> {
    const /** @type {?} */ expanded$ =
        this.expandSegmentGroup(this.ngModule, this.config, this.urlTree.root, PRIMARY_OUTLET);
    const /** @type {?} */ urlTrees$ = map.call(
        expanded$, (rootSegmentGroup: UrlSegmentGroup) => this.createUrlTree(
                       rootSegmentGroup, this.urlTree.queryParams, /** @type {?} */(( this.urlTree.fragment))));
    return _catch.call(urlTrees$, (e: any) => {
      if (e instanceof AbsoluteRedirect) {
        // after an absolute redirect we do not apply any more redirects!
        this.allowRedirects = false;
        // we need to run matching, so we can fetch all lazy-loaded modules
        return this.match(e.urlTree);
      }

      if (e instanceof NoMatch) {
        throw this.noMatchError(e);
      }

      throw e;
    });
  }
/**
 * @param {?} tree
 * @return {?}
 */
private match(tree: UrlTree): Observable<UrlTree> {
    const /** @type {?} */ expanded$ =
        this.expandSegmentGroup(this.ngModule, this.config, tree.root, PRIMARY_OUTLET);
    const /** @type {?} */ mapped$ = map.call(
        expanded$, (rootSegmentGroup: UrlSegmentGroup) =>
                       this.createUrlTree(rootSegmentGroup, tree.queryParams, /** @type {?} */(( tree.fragment))));
    return _catch.call(mapped$, (e: any): Observable<UrlTree> => {
      if (e instanceof NoMatch) {
        throw this.noMatchError(e);
      }

      throw e;
    });
  }
/**
 * @param {?} e
 * @return {?}
 */
private noMatchError(e: NoMatch): any {
    return new Error(`Cannot match any routes. URL Segment: '${e.segmentGroup}'`);
  }
/**
 * @param {?} rootCandidate
 * @param {?} queryParams
 * @param {?} fragment
 * @return {?}
 */
private createUrlTree(rootCandidate: UrlSegmentGroup, queryParams: Params, fragment: string):
      UrlTree {
    const /** @type {?} */ root = rootCandidate.segments.length > 0 ?
        new UrlSegmentGroup([], {[PRIMARY_OUTLET]: rootCandidate}) :
        rootCandidate;
    return new UrlTree(root, queryParams, fragment);
  }
/**
 * @param {?} ngModule
 * @param {?} routes
 * @param {?} segmentGroup
 * @param {?} outlet
 * @return {?}
 */
private expandSegmentGroup(
      ngModule: NgModuleRef<any>, routes: Route[], segmentGroup: UrlSegmentGroup,
      outlet: string): Observable<UrlSegmentGroup> {
    if (segmentGroup.segments.length === 0 && segmentGroup.hasChildren()) {
      return map.call(
          this.expandChildren(ngModule, routes, segmentGroup),
          (children: any) => new UrlSegmentGroup([], children));
    }

    return this.expandSegment(ngModule, segmentGroup, routes, segmentGroup.segments, outlet, true);
  }
/**
 * @param {?} ngModule
 * @param {?} routes
 * @param {?} segmentGroup
 * @return {?}
 */
private expandChildren(
      ngModule: NgModuleRef<any>, routes: Route[],
      segmentGroup: UrlSegmentGroup): Observable<{[name: string]: UrlSegmentGroup}> {
    return waitForMap(
        segmentGroup.children,
        (childOutlet, child) => this.expandSegmentGroup(ngModule, routes, child, childOutlet));
  }
/**
 * @param {?} ngModule
 * @param {?} segmentGroup
 * @param {?} routes
 * @param {?} segments
 * @param {?} outlet
 * @param {?} allowRedirects
 * @return {?}
 */
private expandSegment(
      ngModule: NgModuleRef<any>, segmentGroup: UrlSegmentGroup, routes: Route[],
      segments: UrlSegment[], outlet: string,
      allowRedirects: boolean): Observable<UrlSegmentGroup> {
    const /** @type {?} */ routes$ = of (...routes);
    const /** @type {?} */ processedRoutes$ = map.call(routes$, (r: any) => {
      const /** @type {?} */ expanded$ = this.expandSegmentAgainstRoute(
          ngModule, segmentGroup, routes, r, segments, outlet, allowRedirects);
      return _catch.call(expanded$, (e: any) => {
        if (e instanceof NoMatch) {
          return of (null);
        }

        throw e;
      });
    });
    const /** @type {?} */ concattedProcessedRoutes$ = concatAll.call(processedRoutes$);
    const /** @type {?} */ first$ = first.call(concattedProcessedRoutes$, (s: any) => !!s);
    return _catch.call(first$, (e: any, _: any): Observable<UrlSegmentGroup> => {
      if (e instanceof EmptyError) {
        if (this.noLeftoversInUrl(segmentGroup, segments, outlet)) {
          return of (new UrlSegmentGroup([], {}));
        }

        throw new NoMatch(segmentGroup);
      }

      throw e;
    });
  }
/**
 * @param {?} segmentGroup
 * @param {?} segments
 * @param {?} outlet
 * @return {?}
 */
private noLeftoversInUrl(segmentGroup: UrlSegmentGroup, segments: UrlSegment[], outlet: string):
      boolean {
    return segments.length === 0 && !segmentGroup.children[outlet];
  }
/**
 * @param {?} ngModule
 * @param {?} segmentGroup
 * @param {?} routes
 * @param {?} route
 * @param {?} paths
 * @param {?} outlet
 * @param {?} allowRedirects
 * @return {?}
 */
private expandSegmentAgainstRoute(
      ngModule: NgModuleRef<any>, segmentGroup: UrlSegmentGroup, routes: Route[], route: Route,
      paths: UrlSegment[], outlet: string, allowRedirects: boolean): Observable<UrlSegmentGroup> {
    if (getOutlet(route) !== outlet) {
      return noMatch(segmentGroup);
    }

    if (route.redirectTo === undefined) {
      return this.matchSegmentAgainstRoute(ngModule, segmentGroup, route, paths);
    }

    if (allowRedirects && this.allowRedirects) {
      return this.expandSegmentAgainstRouteUsingRedirect(
          ngModule, segmentGroup, routes, route, paths, outlet);
    }

    return noMatch(segmentGroup);
  }
/**
 * @param {?} ngModule
 * @param {?} segmentGroup
 * @param {?} routes
 * @param {?} route
 * @param {?} segments
 * @param {?} outlet
 * @return {?}
 */
private expandSegmentAgainstRouteUsingRedirect(
      ngModule: NgModuleRef<any>, segmentGroup: UrlSegmentGroup, routes: Route[], route: Route,
      segments: UrlSegment[], outlet: string): Observable<UrlSegmentGroup> {
    if (route.path === '**') {
      return this.expandWildCardWithParamsAgainstRouteUsingRedirect(
          ngModule, routes, route, outlet);
    }

    return this.expandRegularSegmentAgainstRouteUsingRedirect(
        ngModule, segmentGroup, routes, route, segments, outlet);
  }
/**
 * @param {?} ngModule
 * @param {?} routes
 * @param {?} route
 * @param {?} outlet
 * @return {?}
 */
private expandWildCardWithParamsAgainstRouteUsingRedirect(
      ngModule: NgModuleRef<any>, routes: Route[], route: Route,
      outlet: string): Observable<UrlSegmentGroup> {
    const /** @type {?} */ newTree = this.applyRedirectCommands([], /** @type {?} */(( route.redirectTo)), {});
    if ( /** @type {?} */((route.redirectTo)).startsWith('/')) {
      return absoluteRedirect(newTree);
    }

    return mergeMap.call(this.lineralizeSegments(route, newTree), (newSegments: UrlSegment[]) => {
      const /** @type {?} */ group = new UrlSegmentGroup(newSegments, {});
      return this.expandSegment(ngModule, group, routes, newSegments, outlet, false);
    });
  }
/**
 * @param {?} ngModule
 * @param {?} segmentGroup
 * @param {?} routes
 * @param {?} route
 * @param {?} segments
 * @param {?} outlet
 * @return {?}
 */
private expandRegularSegmentAgainstRouteUsingRedirect(
      ngModule: NgModuleRef<any>, segmentGroup: UrlSegmentGroup, routes: Route[], route: Route,
      segments: UrlSegment[], outlet: string): Observable<UrlSegmentGroup> {
    const {matched, consumedSegments, lastChild, positionalParamSegments} =
        match(segmentGroup, route, segments);
    if (!matched) return noMatch(segmentGroup);

    const /** @type {?} */ newTree = this.applyRedirectCommands(
        consumedSegments, /** @type {?} */(( route.redirectTo)), /** @type {?} */(( <any>positionalParamSegments)));
    if ( /** @type {?} */((route.redirectTo)).startsWith('/')) {
      return absoluteRedirect(newTree);
    }

    return mergeMap.call(this.lineralizeSegments(route, newTree), (newSegments: UrlSegment[]) => {
      return this.expandSegment(
          ngModule, segmentGroup, routes, newSegments.concat(segments.slice(lastChild)), outlet,
          false);
    });
  }
/**
 * @param {?} ngModule
 * @param {?} rawSegmentGroup
 * @param {?} route
 * @param {?} segments
 * @return {?}
 */
private matchSegmentAgainstRoute(
      ngModule: NgModuleRef<any>, rawSegmentGroup: UrlSegmentGroup, route: Route,
      segments: UrlSegment[]): Observable<UrlSegmentGroup> {
    if (route.path === '**') {
      if (route.loadChildren) {
        return map.call(
            this.configLoader.load(ngModule.injector, route), (cfg: LoadedRouterConfig) => {
              route._loadedConfig = cfg;
              return new UrlSegmentGroup(segments, {});
            });
      }

      return of (new UrlSegmentGroup(segments, {}));
    }

    const {matched, consumedSegments, lastChild} = match(rawSegmentGroup, route, segments);
    if (!matched) return noMatch(rawSegmentGroup);

    const /** @type {?} */ rawSlicedSegments = segments.slice(lastChild);
    const /** @type {?} */ childConfig$ = this.getChildConfig(ngModule, route);

    return mergeMap.call(childConfig$, (routerConfig: LoadedRouterConfig) => {
      const /** @type {?} */ childModule = routerConfig.module;
      const /** @type {?} */ childConfig = routerConfig.routes;

      const {segmentGroup, slicedSegments} =
          split(rawSegmentGroup, consumedSegments, rawSlicedSegments, childConfig);

      if (slicedSegments.length === 0 && segmentGroup.hasChildren()) {
        const /** @type {?} */ expanded$ = this.expandChildren(childModule, childConfig, segmentGroup);
        return map.call(
            expanded$, (children: any) => new UrlSegmentGroup(consumedSegments, children));
      }

      if (childConfig.length === 0 && slicedSegments.length === 0) {
        return of (new UrlSegmentGroup(consumedSegments, {}));
      }

      const /** @type {?} */ expanded$ = this.expandSegment(
          childModule, segmentGroup, childConfig, slicedSegments, PRIMARY_OUTLET, true);
      return map.call(
          expanded$, (cs: UrlSegmentGroup) =>
                         new UrlSegmentGroup(consumedSegments.concat(cs.segments), cs.children));
    });
  }
/**
 * @param {?} ngModule
 * @param {?} route
 * @return {?}
 */
private getChildConfig(ngModule: NgModuleRef<any>, route: Route): Observable<LoadedRouterConfig> {
    if (route.children) {
      // The children belong to the same module
      return of (new LoadedRouterConfig(route.children, ngModule));
    }

    if (route.loadChildren) {
      // lazy children belong to the loaded module
      if (route._loadedConfig !== undefined) {
        return of (route._loadedConfig);
      }

      return mergeMap.call(runCanLoadGuard(ngModule.injector, route), (shouldLoad: boolean) => {

        if (shouldLoad) {
          return map.call(
              this.configLoader.load(ngModule.injector, route), (cfg: LoadedRouterConfig) => {
                route._loadedConfig = cfg;
                return cfg;
              });
        }

        return canLoadFails(route);
      });
    }

    return of (new LoadedRouterConfig([], ngModule));
  }
/**
 * @param {?} route
 * @param {?} urlTree
 * @return {?}
 */
private lineralizeSegments(route: Route, urlTree: UrlTree): Observable<UrlSegment[]> {
    let /** @type {?} */ res: UrlSegment[] = [];
    let /** @type {?} */ c = urlTree.root;
    while (true) {
      res = res.concat(c.segments);
      if (c.numberOfChildren === 0) {
        return of (res);
      }

      if (c.numberOfChildren > 1 || !c.children[PRIMARY_OUTLET]) {
        return namedOutletsRedirect( /** @type {?} */((route.redirectTo)));
      }

      c = c.children[PRIMARY_OUTLET];
    }
  }
/**
 * @param {?} segments
 * @param {?} redirectTo
 * @param {?} posParams
 * @return {?}
 */
private applyRedirectCommands(
      segments: UrlSegment[], redirectTo: string, posParams: {[k: string]: UrlSegment}): UrlTree {
    return this.applyRedirectCreatreUrlTree(
        redirectTo, this.urlSerializer.parse(redirectTo), segments, posParams);
  }
/**
 * @param {?} redirectTo
 * @param {?} urlTree
 * @param {?} segments
 * @param {?} posParams
 * @return {?}
 */
private applyRedirectCreatreUrlTree(
      redirectTo: string, urlTree: UrlTree, segments: UrlSegment[],
      posParams: {[k: string]: UrlSegment}): UrlTree {
    const /** @type {?} */ newRoot = this.createSegmentGroup(redirectTo, urlTree.root, segments, posParams);
    return new UrlTree(
        newRoot, this.createQueryParams(urlTree.queryParams, this.urlTree.queryParams),
        urlTree.fragment);
  }
/**
 * @param {?} redirectToParams
 * @param {?} actualParams
 * @return {?}
 */
private createQueryParams(redirectToParams: Params, actualParams: Params): Params {
    const /** @type {?} */ res: Params = {};
    forEach(redirectToParams, (v: any, k: string) => {
      const /** @type {?} */ copySourceValue = typeof v === 'string' && v.startsWith(':');
      if (copySourceValue) {
        const /** @type {?} */ sourceName = v.substring(1);
        res[k] = actualParams[sourceName];
      } else {
        res[k] = v;
      }
    });
    return res;
  }
/**
 * @param {?} redirectTo
 * @param {?} group
 * @param {?} segments
 * @param {?} posParams
 * @return {?}
 */
private createSegmentGroup(
      redirectTo: string, group: UrlSegmentGroup, segments: UrlSegment[],
      posParams: {[k: string]: UrlSegment}): UrlSegmentGroup {
    const /** @type {?} */ updatedSegments = this.createSegments(redirectTo, group.segments, segments, posParams);

    let /** @type {?} */ children: {[n: string]: UrlSegmentGroup} = {};
    forEach(group.children, (child: UrlSegmentGroup, name: string) => {
      children[name] = this.createSegmentGroup(redirectTo, child, segments, posParams);
    });

    return new UrlSegmentGroup(updatedSegments, children);
  }
/**
 * @param {?} redirectTo
 * @param {?} redirectToSegments
 * @param {?} actualSegments
 * @param {?} posParams
 * @return {?}
 */
private createSegments(
      redirectTo: string, redirectToSegments: UrlSegment[], actualSegments: UrlSegment[],
      posParams: {[k: string]: UrlSegment}): UrlSegment[] {
    return redirectToSegments.map(
        s => s.path.startsWith(':') ? this.findPosParam(redirectTo, s, posParams) :
                                      this.findOrReturn(s, actualSegments));
  }
/**
 * @param {?} redirectTo
 * @param {?} redirectToUrlSegment
 * @param {?} posParams
 * @return {?}
 */
private findPosParam(
      redirectTo: string, redirectToUrlSegment: UrlSegment,
      posParams: {[k: string]: UrlSegment}): UrlSegment {
    const /** @type {?} */ pos = posParams[redirectToUrlSegment.path.substring(1)];
    if (!pos)
      throw new Error(
          `Cannot redirect to '${redirectTo}'. Cannot find '${redirectToUrlSegment.path}'.`);
    return pos;
  }
/**
 * @param {?} redirectToUrlSegment
 * @param {?} actualSegments
 * @return {?}
 */
private findOrReturn(redirectToUrlSegment: UrlSegment, actualSegments: UrlSegment[]): UrlSegment {
    let /** @type {?} */ idx = 0;
    for (const /** @type {?} */ s of actualSegments) {
      if (s.path === redirectToUrlSegment.path) {
        actualSegments.splice(idx);
        return s;
      }
      idx++;
    }
    return redirectToUrlSegment;
  }
}

function ApplyRedirects_tsickle_Closure_declarations() {
/** @type {?} */
ApplyRedirects.prototype.allowRedirects;
/** @type {?} */
ApplyRedirects.prototype.ngModule;
/** @type {?} */
ApplyRedirects.prototype.configLoader;
/** @type {?} */
ApplyRedirects.prototype.urlSerializer;
/** @type {?} */
ApplyRedirects.prototype.urlTree;
/** @type {?} */
ApplyRedirects.prototype.config;
}

/**
 * @param {?} moduleInjector
 * @param {?} route
 * @return {?}
 */
function runCanLoadGuard(moduleInjector: Injector, route: Route): Observable<boolean> {
  const /** @type {?} */ canLoad = route.canLoad;
  if (!canLoad || canLoad.length === 0) return of (true);

  const /** @type {?} */ obs = map.call(from(canLoad), (injectionToken: any) => {
    const /** @type {?} */ guard = moduleInjector.get(injectionToken);
    return wrapIntoObservable(guard.canLoad ? guard.canLoad(route) : guard(route));
  });

  return andObservables(obs);
}
/**
 * @param {?} segmentGroup
 * @param {?} route
 * @param {?} segments
 * @return {?}
 */
function match(segmentGroup: UrlSegmentGroup, route: Route, segments: UrlSegment[]): {
  matched: boolean,
  consumedSegments: UrlSegment[],
  lastChild: number,
  positionalParamSegments: {[k: string]: UrlSegment}
} {
  if (route.path === '') {
    if ((route.pathMatch === 'full') && (segmentGroup.hasChildren() || segments.length > 0)) {
      return {matched: false, consumedSegments: [], lastChild: 0, positionalParamSegments: {}};
    }

    return {matched: true, consumedSegments: [], lastChild: 0, positionalParamSegments: {}};
  }

  const /** @type {?} */ matcher = route.matcher || defaultUrlMatcher;
  const /** @type {?} */ res = matcher(segments, segmentGroup, route);

  if (!res) {
    return {
      matched: false,
      consumedSegments: /** @type {?} */(( <any[]>[])),
      lastChild: 0,
      positionalParamSegments: {},
    };
  }

  return {
    matched: true,
    consumedSegments: /** @type {?} */(( res.consumed)),
    lastChild: /** @type {?} */(( res.consumed.length)),
    positionalParamSegments: /** @type {?} */(( res.posParams)),
  };
}
/**
 * @param {?} segmentGroup
 * @param {?} consumedSegments
 * @param {?} slicedSegments
 * @param {?} config
 * @return {?}
 */
function split(
    segmentGroup: UrlSegmentGroup, consumedSegments: UrlSegment[], slicedSegments: UrlSegment[],
    config: Route[]) {
  if (slicedSegments.length > 0 &&
      containsEmptyPathRedirectsWithNamedOutlets(segmentGroup, slicedSegments, config)) {
    const /** @type {?} */ s = new UrlSegmentGroup(
        consumedSegments, createChildrenForEmptySegments(
                              config, new UrlSegmentGroup(slicedSegments, segmentGroup.children)));
    return {segmentGroup: mergeTrivialChildren(s), slicedSegments: []};
  }

  if (slicedSegments.length === 0 &&
      containsEmptyPathRedirects(segmentGroup, slicedSegments, config)) {
    const /** @type {?} */ s = new UrlSegmentGroup(
        segmentGroup.segments, addEmptySegmentsToChildrenIfNeeded(
                                   segmentGroup, slicedSegments, config, segmentGroup.children));
    return {segmentGroup: mergeTrivialChildren(s), slicedSegments};
  }

  return {segmentGroup, slicedSegments};
}
/**
 * @param {?} s
 * @return {?}
 */
function mergeTrivialChildren(s: UrlSegmentGroup): UrlSegmentGroup {
  if (s.numberOfChildren === 1 && s.children[PRIMARY_OUTLET]) {
    const /** @type {?} */ c = s.children[PRIMARY_OUTLET];
    return new UrlSegmentGroup(s.segments.concat(c.segments), c.children);
  }

  return s;
}
/**
 * @param {?} segmentGroup
 * @param {?} slicedSegments
 * @param {?} routes
 * @param {?} children
 * @return {?}
 */
function addEmptySegmentsToChildrenIfNeeded(
    segmentGroup: UrlSegmentGroup, slicedSegments: UrlSegment[], routes: Route[],
    children: {[name: string]: UrlSegmentGroup}): {[name: string]: UrlSegmentGroup} {
  const /** @type {?} */ res: {[name: string]: UrlSegmentGroup} = {};
  for (const /** @type {?} */ r of routes) {
    if (isEmptyPathRedirect(segmentGroup, slicedSegments, r) && !children[getOutlet(r)]) {
      res[getOutlet(r)] = new UrlSegmentGroup([], {});
    }
  }
  return {...children, ...res};
}
/**
 * @param {?} routes
 * @param {?} primarySegmentGroup
 * @return {?}
 */
function createChildrenForEmptySegments(
    routes: Route[], primarySegmentGroup: UrlSegmentGroup): {[name: string]: UrlSegmentGroup} {
  const /** @type {?} */ res: {[name: string]: UrlSegmentGroup} = {};
  res[PRIMARY_OUTLET] = primarySegmentGroup;
  for (const /** @type {?} */ r of routes) {
    if (r.path === '' && getOutlet(r) !== PRIMARY_OUTLET) {
      res[getOutlet(r)] = new UrlSegmentGroup([], {});
    }
  }
  return res;
}
/**
 * @param {?} segmentGroup
 * @param {?} segments
 * @param {?} routes
 * @return {?}
 */
function containsEmptyPathRedirectsWithNamedOutlets(
    segmentGroup: UrlSegmentGroup, segments: UrlSegment[], routes: Route[]): boolean {
  return routes.some(
      r => isEmptyPathRedirect(segmentGroup, segments, r) && getOutlet(r) !== PRIMARY_OUTLET);
}
/**
 * @param {?} segmentGroup
 * @param {?} segments
 * @param {?} routes
 * @return {?}
 */
function containsEmptyPathRedirects(
    segmentGroup: UrlSegmentGroup, segments: UrlSegment[], routes: Route[]): boolean {
  return routes.some(r => isEmptyPathRedirect(segmentGroup, segments, r));
}
/**
 * @param {?} segmentGroup
 * @param {?} segments
 * @param {?} r
 * @return {?}
 */
function isEmptyPathRedirect(
    segmentGroup: UrlSegmentGroup, segments: UrlSegment[], r: Route): boolean {
  if ((segmentGroup.hasChildren() || segments.length > 0) && r.pathMatch === 'full') {
    return false;
  }

  return r.path === '' && r.redirectTo !== undefined;
}
/**
 * @param {?} route
 * @return {?}
 */
function getOutlet(route: Route): string {
  return route.outlet || PRIMARY_OUTLET;
}



// WEBPACK FOOTER //
// ../../../../packages/router/src/apply_redirects.ts